import { compare, money, type Money } from "@stb/core/money"
import {
  GST_REGISTRATION,
  INCOME_TAX_YEARS,
  incomeTaxYear,
  type Residency,
} from "@stb/jurisdiction-au/income-tax"
import {
  Briefcase,
  Calculator,
  ChevronDown,
  CircleAlert,
  Copy,
  GraduationCap,
  HeartPulse,
  Landmark,
  Lock,
  PiggyBank,
  Receipt,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react"
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react"

import type { Locale } from "@/core/i18n/locale"
import { useLocale } from "@/core/i18n/locale-context"
import { Button } from "@/ui/button"
import { Label } from "@/ui/label"

import { SOLE_CALC_COPY_ZH } from "../../content/zh/sole-trader-calculator-copy"
import { displayYear } from "../home/home-content"
import {
  CalcHeading,
  CalcIconChip,
  CalcShortcuts,
  Kbd,
  calcEnter,
  calcHeroInput,
  calcInputPanel,
  calcPanels,
  calcResultFigure,
  calcResultPanel,
  calcResultSlot,
  type CalcShortcut,
  type ChipTone,
} from "./calc-ui"
import { formatAud } from "./calculator"
import { normalizeAmountInput, stepAmount } from "./pay-calculator-model"
import { PROVISIONAL_TEXT } from "./provisional-text"
import {
  DEFAULT_SOLE_FORM,
  evaluateSole,
  formToSoleParams,
  parseSoleForm,
  soleShares,
  type SoleForm,
  type SolePeriod,
  type SoleShow,
} from "./sole-trader-calculator-model"
import { SoleCompareVisual, SoleSplitVisual } from "./sole-trader-calculator-visual"

/** Everything the calculator prints, by language. Numbers come from the engine and are passed in. */
export interface SoleCalcCopy {
  periods: Record<SolePeriod, string>
  periodNoun: Record<SolePeriod, string>
  shows: Record<SoleShow, string>
  shortcuts: CalcShortcut[]
  heading: string
  paidLegend: string
  incomeLabel: (noun: string) => string
  hoursLabel: string
  daysLabel: string
  weeksLabel: string
  expensesLabel: string
  wagesLabel: string
  hint: string
  moreOptions: string
  moreOptionsDetail: string
  incomeYear: string
  yearOption: (displayYear: string) => string
  residency: string
  residencyOptions: Record<Residency, string>
  helpBox: string
  medicareBox: (rate: string) => string
  keysHint: { change: string; ten: string; all: string }
  resultHeading: string
  showLegend: string
  headline: {
    month: (fy: string) => string
    quarter: (fy: string) => string
    empty: string
    invalid: string
  }
  live: (headline: string, figure: string) => string
  share: (ofIncome: string, ofProfit: string) => string
  noProfit: string
  tableCaption: string
  rows: {
    income: string
    expenses: string
    profit: string
    incomeTax: string
    medicare: string
    help: string
    taxYear: string
    takeHome: string
  }
  loss: string
  provisional: string
  provisionalFallback: string
  copyLink: string
  copied: string
  copyFailed: string
  local: string
  disclaimerBefore: string
  disclaimerLink: string
  disclaimerAfter: string
  gstHeading: string
  gstRequired: (cap: string) => string
  gstOptional: (cap: string) => string
  gstUnknown: (cap: string) => string
  gstNote: string
  gstLink: string
  gstNoteEnd: string
  superHeading: string
  superText: (cap: string, fy: string) => string
  superLink: string
  superLinkEnd: string
}

const SOLE_CALC_COPY_EN: SoleCalcCopy = {
  periods: { year: "Year", month: "Month", week: "Week", day: "Day", hour: "Hour" },
  periodNoun: {
    year: "a year",
    month: "a month",
    week: "a week",
    day: "a day",
    hour: "an hour",
  },
  shows: { month: "Each month", quarter: "Each quarter" },
  shortcuts: [
    { keys: ["↑", "↓"], separator: "/", action: "Change the income by one step" },
    { keys: ["Shift", "↑ ↓"], action: "Ten steps" },
    { keys: ["Alt", "↑ ↓"], action: "A tenth of a step" },
    { keys: ["←", "→"], separator: "/", action: "Move between pay periods" },
    { keys: ["?"], action: "Show these shortcuts" },
    { keys: ["Esc"], action: "Close this list" },
  ],
  heading: "Your business",
  paidLegend: "I am paid",
  incomeLabel: (noun) => `Business income ${noun}, before GST`,
  hoursLabel: "Hours a week",
  daysLabel: "Days a week",
  weeksLabel: "Weeks worked a year",
  expensesLabel: "Business expenses a year",
  wagesLabel: "Wages from a job, a year",
  hint: "Before GST. Your expenses and any wages are for the whole year.",
  moreOptions: "More options",
  moreOptionsDetail: "(year, residency, HELP, Medicare)",
  incomeYear: "Income year",
  yearOption: (y) => y,
  residency: "Tax residency",
  residencyOptions: {
    resident: "Australian resident",
    foreign: "Foreign resident",
    "working-holiday": "Working holiday maker",
  },
  helpBox: "I have a HELP or other study loan",
  medicareBox: (rate) => `Include the ${rate} Medicare levy`,
  keysHint: { change: "change the income", ten: "×10", all: "all shortcuts" },
  resultHeading: "Set aside for tax",
  showLegend: "Show the amount",
  headline: {
    month: (fy) => `Set aside each month for tax in ${fy}`,
    quarter: (fy) => `Set aside each quarter for tax in ${fy}`,
    empty: "Enter your income",
    invalid: "Check what you entered",
  },
  live: (headline, figure) => `${headline}: ${figure}`,
  share: (ofIncome, ofProfit) => `About ${ofIncome} of what you earn, ${ofProfit} of your profit`,
  noProfit: "No profit, so no tax to set aside",
  tableCaption: "Business tax breakdown for the year",
  rows: {
    income: "Business income",
    expenses: "Business expenses",
    profit: "Profit",
    incomeTax: "Income tax (after offsets)",
    medicare: "Medicare levy",
    help: "HELP repayment",
    taxYear: "Tax to set aside, a year",
    takeHome: "Take-home after tax",
  },
  loss: "Your expenses are higher than your income. The calculator shows no profit and does not set the loss against your wages.",
  provisional: "Provisional.",
  provisionalFallback: "Some rules are provisional.",
  copyLink: "Copy link to this result",
  copied: "Link copied",
  copyFailed: "Could not copy. Copy the address bar instead.",
  local: "Runs in your browser. Nothing is sent to us.",
  disclaimerBefore:
    "Estimate only, not tax advice. Single person, no dependants, no other deductions or offsets. Not included: the small business income tax offset, PAYG instalments (the ATO sends the amounts), the Medicare levy surcharge and the standard deduction. HELP uses taxable income only. Check the ",
  disclaimerLink: "ATO income tax estimator",
  disclaimerAfter: " or a registered tax agent for your own return.",
  gstHeading: "GST",
  gstRequired: (c) =>
    `Your business income is ${c} or more, so you are likely to need to register for GST. Taxi and ride-sourcing drivers must register at any turnover.`,
  gstOptional: (c) =>
    `Your business income is under ${c}, so registering for GST is generally optional. You must register once your GST turnover reaches it, and taxi and ride-sourcing drivers must register at any turnover.`,
  gstUnknown: (c) => `You must register for GST once your GST turnover reaches ${c}.`,
  gstNote: "This calculator uses income before GST. GST you collect is not your income. ",
  gstLink: "ATO, registering for GST",
  gstNoteEnd: ".",
  superHeading: "Super",
  superText: (c, fy) =>
    `This calculator assumes no employer pays super on your business income. If you put money into super yourself and claim it as a deduction, it counts towards the ${c} before-tax cap for ${fy}, together with any super your employer pays on your wages. This calculator does not include voluntary contributions.`,
  superLink: "ATO, contributions caps",
  superLinkEnd: ".",
}

const CALC_COPY: Record<Locale, SoleCalcCopy> = { en: SOLE_CALC_COPY_EN, zh: SOLE_CALC_COPY_ZH }
const PERIOD_VALUES: SolePeriod[] = ["year", "month", "week", "day", "hour"]
const SHOW_VALUES: SoleShow[] = ["month", "quarter"]
const AMOUNT_KEYS = "ArrowUp ArrowDown Shift+ArrowUp Shift+ArrowDown Alt+ArrowUp Alt+ArrowDown"

const selectClass =
  "border-input bg-background dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-11 w-full rounded-md border px-3 text-base outline-none focus-visible:ring-[3px]"
const textFieldClass =
  "border-input bg-background dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive h-11 w-full min-w-0 rounded-md border px-3 text-base tabular-nums outline-none focus-visible:ring-[3px]"
const DASH = "–"
const ATO_ESTIMATOR_URL = "https://www.ato.gov.au/calculators-and-tools/income-tax-estimator"

const isZero = (value: Money) => compare(value, money(0, "AUD")) === 0
const minus = (value: Money) => (isZero(value) ? formatAud(value) : `−${formatAud(value)}`)
const percent = (bp: number) =>
  `${new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1 }).format(bp / 100)}%`
const cap = (dollars: number) => `A$${dollars.toLocaleString("en-AU")}`

function arrowStep(e: KeyboardEvent<HTMLInputElement>): 1 | -1 | null {
  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return null
  return e.key === "ArrowUp" ? 1 : -1
}

/** A labelled number field under the hero box; the hint or error text sits in one shared line below the inputs. */
function Field({
  id,
  label,
  value,
  onChange,
  invalid,
  describedBy,
  prefix,
  inputMode = "decimal",
  wide,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  invalid: boolean
  describedBy: string
  prefix?: string
  inputMode?: "decimal" | "numeric"
  wide?: boolean
}) {
  return (
    <div className={`grid gap-1.5 ${wide ? "col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-sm">
        {label}
      </Label>
      <div className="relative">
        {prefix && (
          <span
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            aria-hidden="true"
          >
            {prefix}
          </span>
        )}
        <input
          id={id}
          inputMode={inputMode}
          autoComplete="off"
          className={`${textFieldClass} ${prefix ? "pl-10" : ""}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid}
          aria-describedby={invalid ? describedBy : undefined}
        />
      </div>
    </div>
  )
}

/**
 * The interactive part of /sole-trader-tax-calculator (T916). Runs in the browser; the URL holds the form (no history
 * entries). Same look as the GST and pay calculators: input panel on the card surface, result panel on a success tint
 * at the same height, every state keeps the same rows so typing never moves the layout. Keys are handled on the
 * calculator root (never on document): arrows step the income, "?" lists the shortcuts.
 */
export function SoleTraderCalculator() {
  const locale = useLocale()
  const t = CALC_COPY[locale]
  const id = useId()
  const [form, setForm] = useState<SoleForm>(DEFAULT_SOLE_FORM)
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null)
  const [keysOpen, setKeysOpen] = useState(false)
  // No motion on load or for the prerendered example: only after the user changes something.
  const [animate, setAnimate] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const keysButtonRef = useRef<HTMLButtonElement>(null)
  const keysId = `${id}-keys`
  const hintId = `${id}-hint`

  // Restore a shared link after hydration, so the prerendered HTML always shows the default example.
  useEffect(() => {
    if (!window.location.search) return
    // oxlint-disable-next-line react/set-state-in-effect -- reading the URL is the external system; doing it in render would mismatch the prerendered HTML
    setForm(parseSoleForm(new URLSearchParams(window.location.search)))
  }, [])

  // Move focus into the shortcuts list when it opens, so screen readers announce it.
  useEffect(() => {
    if (keysOpen) document.getElementById(keysId)?.focus()
  }, [keysOpen, keysId])

  const update = (next: Partial<SoleForm>) => {
    const merged = { ...form, ...next }
    setForm(merged)
    setCopied(null)
    setAnimate(true)
    const query = formToSoleParams(merged).toString()
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`)
  }
  const updateAmount = (key: "income" | "expenses" | "wages", text: string) =>
    update({ [key]: normalizeAmountInput(text) })

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied("ok")
    } catch {
      setCopied("failed")
    }
  }

  const closeKeys = (returnFocus: boolean) => {
    setKeysOpen(false)
    if (returnFocus) keysButtonRef.current?.focus()
  }
  const onRootKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      setKeysOpen(true)
    } else if (e.key === "Escape" && keysOpen) {
      e.preventDefault()
      closeKeys(true)
    }
  }

  const outcome = evaluateSole(form, locale)
  const ok = outcome.kind === "ok" ? outcome : null
  const r = ok?.result
  const invalid = outcome.kind === "error" ? outcome : null
  const year = incomeTaxYear(form.fy)
  const fy = displayYear(form.fy)

  const setAside = r ? (form.show === "month" ? r.setAside.perMonth : r.setAside.perQuarter) : null
  const figure = setAside ? formatAud(setAside) : DASH
  const headline = r
    ? t.headline[form.show](fy)
    : form.income.trim() === ""
      ? t.headline.empty
      : t.headline.invalid

  const rows: [LucideIcon, ChipTone, string, string, boolean][] = [
    [Briefcase, "muted", t.rows.income, ok ? formatAud(ok.annualIncome) : DASH, false],
    [Receipt, "muted", t.rows.expenses, ok ? minus(ok.expenses) : DASH, false],
    [TrendingUp, "primary", t.rows.profit, r ? formatAud(r.profit) : DASH, false],
    [Landmark, "primary", t.rows.incomeTax, r ? minus(r.business.incomeTaxAfterLito) : DASH, false],
    [HeartPulse, "primary", t.rows.medicare, r ? minus(r.business.medicareLevy) : DASH, false],
    ...(form.help
      ? ([
          [
            GraduationCap,
            "primary",
            t.rows.help,
            r ? minus(r.business.studyLoanRepayment) : DASH,
            false,
          ],
        ] as [LucideIcon, ChipTone, string, string, boolean][])
      : []),
    [PiggyBank, "success", t.rows.taxYear, r ? formatAud(r.business.total) : DASH, true],
    [Wallet, "success", t.rows.takeHome, r ? formatAud(r.takeHome) : DASH, true],
  ]

  const hint = invalid ? invalid.message : t.hint
  const bad = invalid?.field
  const parts = ok ? soleShares(ok.annualIncome.amount, ok.expenses.amount, ok.result) : null

  return (
    <div
      ref={rootRef}
      className="grid gap-4"
      onKeyDown={onRootKeyDown}
      onBlur={(e) => {
        if (keysOpen && !rootRef.current?.contains(e.relatedTarget)) closeKeys(false)
      }}
    >
      <div className={calcPanels}>
        <div className={calcInputPanel}>
          <div className="flex items-center justify-between gap-3">
            <CalcHeading icon={Calculator} tone="primary">
              {t.heading}
            </CalcHeading>
            <CalcShortcuts
              ref={keysButtonRef}
              id={keysId}
              shortcuts={t.shortcuts}
              open={keysOpen}
              onToggle={() => setKeysOpen((open) => !open)}
            />
          </div>

          <div className="border-input bg-background dark:bg-input/30 has-[[aria-invalid=true]]:border-destructive focus-within:border-ring focus-within:ring-ring/50 overflow-hidden rounded-xl border focus-within:ring-[3px]">
            <fieldset className="bg-muted m-0 border-0 p-1">
              <legend className="sr-only">{t.paidLegend}</legend>
              <div className="grid grid-cols-5 gap-1">
                {PERIOD_VALUES.map((pe) => (
                  <label key={pe} className="relative">
                    <input
                      type="radio"
                      name={`${id}-period`}
                      value={pe}
                      className="peer sr-only"
                      checked={form.period === pe}
                      onChange={() => update({ period: pe })}
                      aria-keyshortcuts="ArrowLeft ArrowRight"
                    />
                    <span className="text-muted-foreground peer-checked:bg-card peer-checked:text-primary peer-checked:ring-primary peer-focus-visible:ring-ring flex min-h-11 cursor-pointer items-center justify-center rounded-lg px-1 text-sm font-semibold peer-checked:shadow-sm peer-checked:ring-[1.5px] peer-focus-visible:ring-[3px]">
                      {t.periods[pe]}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="grid px-3.5 pt-2 pb-0.5">
              <Label htmlFor={`${id}-income`} className="text-muted-foreground text-sm">
                {t.incomeLabel(t.periodNoun[form.period])}
              </Label>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground text-lg font-semibold" aria-hidden="true">
                  A$
                </span>
                <input
                  id={`${id}-income`}
                  inputMode="decimal"
                  autoComplete="off"
                  className={calcHeroInput}
                  value={form.income}
                  onChange={(e) => updateAmount("income", e.target.value)}
                  onKeyDown={(e) => {
                    const dir = arrowStep(e)
                    if (!dir) return
                    e.preventDefault()
                    const next = stepAmount(form.income, form.period, dir, {
                      shift: e.shiftKey,
                      alt: e.altKey,
                    })
                    if (next !== undefined) update({ income: next })
                  }}
                  aria-invalid={bad === "income"}
                  aria-describedby={hintId}
                  aria-keyshortcuts={AMOUNT_KEYS}
                />
              </div>
            </div>
          </div>

          {(form.period === "hour" || form.period === "day" || form.period === "week") && (
            <div className="grid grid-cols-2 gap-3">
              {form.period === "hour" && (
                <Field
                  id={`${id}-hours`}
                  label={t.hoursLabel}
                  value={form.hoursPerWeek}
                  onChange={(v) => update({ hoursPerWeek: v })}
                  invalid={bad === "hours"}
                  describedBy={hintId}
                />
              )}
              {form.period === "day" && (
                <Field
                  id={`${id}-days`}
                  label={t.daysLabel}
                  value={form.daysPerWeek}
                  onChange={(v) => update({ daysPerWeek: v })}
                  invalid={bad === "days"}
                  describedBy={hintId}
                  inputMode="numeric"
                />
              )}
              <Field
                id={`${id}-weeks`}
                label={t.weeksLabel}
                value={form.weeksPerYear}
                onChange={(v) => update({ weeksPerYear: v })}
                invalid={bad === "weeks"}
                describedBy={hintId}
                inputMode="numeric"
              />
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id={`${id}-expenses`}
              label={t.expensesLabel}
              value={form.expenses}
              onChange={(v) => updateAmount("expenses", v)}
              invalid={bad === "expenses"}
              describedBy={hintId}
              prefix="A$"
            />
            <Field
              id={`${id}-wages`}
              label={t.wagesLabel}
              value={form.wages}
              onChange={(v) => updateAmount("wages", v)}
              invalid={bad === "wages"}
              describedBy={hintId}
              prefix="A$"
            />
          </div>

          <p
            id={hintId}
            className={`-mt-1 flex min-h-10 items-start gap-1.5 text-sm ${invalid ? "text-destructive font-medium" : "text-muted-foreground"}`}
          >
            {invalid && <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />}
            {hint}
          </p>

          <details className="group border-border border-t pt-1">
            <summary className="text-primary focus-visible:ring-ring flex min-h-11 w-fit cursor-pointer list-none items-center gap-1.5 rounded-md text-sm font-medium outline-none focus-visible:ring-[3px] [&::-webkit-details-marker]:hidden">
              <ChevronDown
                className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none"
                aria-hidden="true"
              />
              {t.moreOptions}
              <span className="text-muted-foreground font-normal">{t.moreOptionsDetail}</span>
            </summary>
            <div className="mt-2 grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor={`${id}-fy`} className="text-sm">
                    {t.incomeYear}
                  </Label>
                  <select
                    id={`${id}-fy`}
                    className={selectClass}
                    value={form.fy}
                    onChange={(e) => update({ fy: e.target.value })}
                  >
                    {INCOME_TAX_YEARS.toReversed().map((y) => (
                      <option key={y.financialYear} value={y.financialYear}>
                        {t.yearOption(displayYear(y.financialYear))}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor={`${id}-res`} className="text-sm">
                    {t.residency}
                  </Label>
                  <select
                    id={`${id}-res`}
                    className={selectClass}
                    value={form.residency}
                    onChange={(e) => update({ residency: e.target.value as SoleForm["residency"] })}
                  >
                    <option value="resident">{t.residencyOptions.resident}</option>
                    <option value="foreign">{t.residencyOptions.foreign}</option>
                    <option value="working-holiday">{t.residencyOptions["working-holiday"]}</option>
                  </select>
                </div>
              </div>
              <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  className="accent-primary size-4 shrink-0"
                  checked={form.help}
                  onChange={(e) => update({ help: e.target.checked })}
                />
                {t.helpBox}
              </label>
              <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  className="accent-primary size-4 shrink-0"
                  checked={form.medicare}
                  onChange={(e) => update({ medicare: e.target.checked })}
                />
                {t.medicareBox(percent(year.medicare.rateBp))}
              </label>
            </div>
          </details>

          <p className="text-muted-foreground mt-auto hidden flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] md:flex">
            <Kbd>↑</Kbd>
            <span>/</span>
            <Kbd>↓</Kbd>
            <span>{t.keysHint.change}</span>
            <span aria-hidden="true">·</span>
            <Kbd>Shift</Kbd>
            <span>{t.keysHint.ten}</span>
            <span aria-hidden="true">·</span>
            <Kbd>?</Kbd>
            <span>{t.keysHint.all}</span>
          </p>
        </div>

        <div data-testid="calc-result" className={calcResultPanel}>
          <span data-testid="calc-live" aria-live="polite" aria-atomic="true" className="sr-only">
            {t.live(headline, figure)}
          </span>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CalcHeading icon={PiggyBank} tone="success">
              {t.resultHeading}
            </CalcHeading>
            <fieldset className="bg-muted m-0 border-0 p-0.5">
              <legend className="sr-only">{t.showLegend}</legend>
              <div className="grid grid-cols-2 gap-0.5">
                {SHOW_VALUES.map((s) => (
                  <label key={s} className="relative">
                    <input
                      type="radio"
                      name={`${id}-show`}
                      value={s}
                      className="peer sr-only"
                      checked={form.show === s}
                      onChange={() => update({ show: s })}
                    />
                    <span className="text-muted-foreground peer-checked:bg-card peer-checked:text-primary peer-checked:ring-primary peer-focus-visible:ring-ring flex min-h-11 cursor-pointer items-center justify-center rounded-md px-3 text-sm font-semibold peer-checked:shadow-sm peer-checked:ring-[1.5px] peer-focus-visible:ring-[3px]">
                      {t.shows[s]}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
          <div className="grid gap-0.5">
            <span data-testid="calc-headline" className="text-muted-foreground text-sm">
              {headline}
            </span>
            <div className={calcResultSlot}>
              <span key={figure} className={`${calcResultFigure} ${calcEnter(animate)}`}>
                {figure}
              </span>
            </div>
            <span className="text-muted-foreground min-h-5 text-sm tabular-nums">
              {r &&
                (r.profit.amount > 0
                  ? t.share(
                      percent(r.setAside.shareOfIncomeBp),
                      percent(r.setAside.shareOfProfitBp),
                    )
                  : t.noProfit)}
            </span>
          </div>
          <table className="bg-card w-full rounded-md text-[15px]">
            <caption className="sr-only">{t.tableCaption}</caption>
            <tbody>
              {rows.map(([icon, tone, label, value, strong]) => (
                <tr key={label} className="border-border border-b last:border-b-0">
                  <th
                    scope="row"
                    className={`py-2.5 pr-3 pl-3 text-left ${strong ? "font-bold" : "font-normal"}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <CalcIconChip icon={icon} tone={tone} />
                      {label}
                    </span>
                  </th>
                  <td
                    className={`py-2.5 pr-3 text-right tabular-nums ${strong ? "font-bold" : ""}`}
                  >
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {r?.loss && <p className="text-muted-foreground text-sm">{t.loss}</p>}
          {r && r.provisional.length > 0 && (
            <ul className="grid gap-1.5">
              {r.provisional.map((name) => (
                <li
                  key={name}
                  className="bg-warning-soft text-warning flex items-start gap-1.5 rounded-md px-2.5 py-1.5 text-sm"
                >
                  <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>
                    <b>{t.provisional}</b>{" "}
                    {PROVISIONAL_TEXT[name]?.(form.fy, locale) ?? t.provisionalFallback}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <Button
              type="button"
              variant="outline"
              className="h-11"
              onClick={copyLink}
              disabled={!r}
            >
              <Copy aria-hidden="true" />
              {t.copyLink}
            </Button>
            <span className="text-muted-foreground text-sm">
              {copied === "ok" && t.copied}
              {copied === "failed" && t.copyFailed}
            </span>
          </div>
          <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <Lock className="size-3.5 shrink-0" aria-hidden="true" />
            {t.local}
          </p>
          <p className="text-muted-foreground mt-auto text-[13px] leading-snug">
            {t.disclaimerBefore}
            <a
              href={ATO_ESTIMATOR_URL}
              className="text-primary underline underline-offset-2"
              rel="noreferrer"
            >
              {t.disclaimerLink}
            </a>
            {t.disclaimerAfter}
          </p>
        </div>
      </div>

      <SoleSplitVisual id={id} parts={parts} income={ok?.annualIncome ?? null} animate={animate} />
      <SoleCompareVisual
        income={ok?.annualIncome ?? null}
        expenses={ok?.expenses ?? null}
        businessTax={r?.business.total ?? null}
        takeHome={r?.takeHome ?? null}
        employee={ok?.employee ?? null}
      />

      <div className="grid gap-3 md:grid-cols-2">
        <aside
          aria-labelledby={`${id}-gst`}
          data-testid="sole-gst-note"
          className="bg-card border-border grid content-start gap-1.5 rounded-xl border p-4 md:p-5"
        >
          <h3 id={`${id}-gst`} className="text-base font-semibold">
            {t.gstHeading}
          </h3>
          <p className="text-sm" data-testid="sole-gst-text">
            {r
              ? r.gstRegistrationRequired
                ? t.gstRequired(cap(GST_REGISTRATION.thresholdDollars))
                : t.gstOptional(cap(GST_REGISTRATION.thresholdDollars))
              : t.gstUnknown(cap(GST_REGISTRATION.thresholdDollars))}
          </p>
          <p className="text-muted-foreground text-sm">
            {t.gstNote}
            <a
              href={GST_REGISTRATION.source.url}
              className="text-primary underline underline-offset-2"
              rel="noreferrer"
            >
              {t.gstLink}
            </a>
            {t.gstNoteEnd}
          </p>
        </aside>
        <aside
          aria-labelledby={`${id}-super`}
          className="bg-card border-border grid content-start gap-1.5 rounded-xl border p-4 md:p-5"
        >
          <h3 id={`${id}-super`} className="text-base font-semibold">
            {t.superHeading}
          </h3>
          <p className="text-sm">{t.superText(cap(year.concessionalCap.dollars), fy)}</p>
          <p className="text-muted-foreground text-sm">
            <a
              href={year.concessionalCap.source.url}
              className="text-primary underline underline-offset-2"
              rel="noreferrer"
            >
              {t.superLink}
            </a>
            {t.superLinkEnd}
          </p>
        </aside>
      </div>
    </div>
  )
}
